import type { RefObject } from 'preact';
import { useEffect, useCallback, useRef } from 'preact/hooks';
import { ListProps as CommonProps } from '#UNSAFE_ListView/List.types';
import { scrollToVisible } from '#PRIVATE_List/ListUtils';
import { useCurrentKey } from '#hooks/PRIVATE_useCurrentKey';
import { useCurrentItemOverride } from './useCurrentItemOverride';
import { useCollectionFocusRing } from '#hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '#hooks/PRIVATE_useTabbableModeSet';
import { getFirstVisibleKey, keyExtractor, findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { isKeyDefined } from '#utils/UNSAFE_keys';

type ArrowKeyHandler<K> = (key?: K) => () => K | null;
type ArrowKeyAndHandlers<K> = {
  ArrowUp: ArrowKeyHandler<K>;
  ArrowDown: ArrowKeyHandler<K>;
  ArrowLeft?: ArrowKeyHandler<K>;
  ArrowRight?: ArrowKeyHandler<K>;
};

/**
 * A hook to set up the currentKey, focus ring and F2 mode.
 *
 * The current key setup includes convert the currentItemOverride to currentKey syntax, set initial currentKey,
 * scroll current item into view, update aria-activedescendant when currentKey changed.
 *
 * This hook can be shared across ListView, CardView, TreeView. Each component should pass in the item selector.
 */
export const useNavigation = <K extends string | number, D>(
  data: D,
  rootRef: RefObject<HTMLDivElement>,
  idExtractor: (elem: Element) => string,
  itemSelector: string,
  arrowKeyAndHandlers: ArrowKeyAndHandlers<K>,
  currentItemOverride: CommonProps<K, D>['currentItemOverride'],
  onPersistCurrentItem: CommonProps<K, D>['onPersistCurrentItem'],
  viewportConfig: CommonProps<K, D>['viewportConfig'],
  scrollToVisibleOffset: CommonProps<K, D>['scrollToVisibleOffset'],
  allowShiftKey: boolean,
  allowTabbableMode?: boolean,
  allowActiveDescendant?: boolean,
  isEmbedded?: boolean,
  allowEnterKey?: boolean
) => {
  // tracking pending current key in pointer down event
  const pendingCurrentKey = useRef<K>();
  // ensure current item scroll into view when treeview regains focus
  // unless pendingCurrentKey gets updated to something different than currentKey
  const focusAndScrollIntoView = useRef(true);

  const { currentKey, onCurrentKeyChange, isCurrentItemOverridden } = useCurrentItemOverride(
    currentItemOverride,
    onPersistCurrentItem
  );

  const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
    rootRef,
    (element) => {
      // we don't want the component to go into Tabbable mode when user clicks on expander
      return isExpander(element) ? null : (keyExtractor(element, itemSelector) as K);
    },
    currentKey,
    onCurrentKeyChange,
    undefined,
    undefined,
    allowEnterKey
  );

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, itemSelector),
    allowShiftKey,
    isCurrentTabbableKey(currentKey),
    arrowKeyAndHandlers.ArrowUp(currentKey),
    arrowKeyAndHandlers.ArrowDown(currentKey),
    arrowKeyAndHandlers.ArrowLeft?.(currentKey),
    arrowKeyAndHandlers.ArrowRight?.(currentKey),
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(
    rootRef,
    Object.keys(arrowKeyAndHandlers)
  );

  // need to track pointer down element to set currentKey when initial focus happens
  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      const key = keyExtractor(event.target as HTMLElement, itemSelector);
      if (isKeyDefined(key)) {
        pendingCurrentKey.current = key as K;
        if (key !== currentKey) {
          focusAndScrollIntoView.current = false;
        }
      }
    },
    [currentKey]
  );

  // handle undefined currentKey at the initial focus or invalid currentKey that doesn't exist
  const handleInvalidCurrentKey = useCallback(() => {
    if (rootRef.current && (!allowTabbableMode || isCurrentTabbableKey(undefined))) {
      const firstKey =
        pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, itemSelector) as K);
      if (isKeyDefined(firstKey)) {
        onCurrentKeyChange({ value: firstKey });
      }
    }
  }, [rootRef, currentKey, onCurrentKeyChange, allowTabbableMode, isCurrentTabbableKey]);

  // handle currenKey and scroll into view during focus
  const handleFocus = useCallback(
    (event: FocusEvent) => {
      // avoid the focus get trapped in the expander
      if (isExpander(event.target as HTMLElement)) {
        rootRef.current?.focus({ preventScroll: true });
        return;
      }
      if (rootRef.current) {
        if (!isKeyDefined(currentKey)) {
          handleInvalidCurrentKey();
        } else {
          const elem = findElementByKey(rootRef.current, currentKey!, itemSelector);
          if (elem) {
            // if currentKey is defined, make sure scroll the current item into view only when the component regains focus,
            // unless pendingCurrentKey gets updated to something different than currentKey
            if (focusAndScrollIntoView.current) {
              scrollIntoView(elem, rootRef, viewportConfig, scrollToVisibleOffset);
            }
            focusAndScrollIntoView.current = true;
          } else {
            // if elem is not found, meaning the currentKey is not valid,
            // we should pick the first item as current item
            handleInvalidCurrentKey();
          }
        }
      }
    },
    [rootRef, currentKey, handleInvalidCurrentKey, viewportConfig, scrollToVisibleOffset]
  );

  // update aria-activedescendant for screen reader
  useEffect(() => {
    if (allowActiveDescendant && rootRef.current && isKeyDefined(currentKey)) {
      const elem = findElementByKey(rootRef.current, currentKey!, itemSelector);
      if (elem) {
        const id = idExtractor(elem);
        const activeDescendant = rootRef.current.getAttribute('aria-activedescendant');
        if (id !== '' && activeDescendant !== id) {
          rootRef.current.setAttribute('aria-activedescendant', id);
          // scroll the current item into view only when:
          // 1. the current item is updated internally and listview has focus
          // 2. the current item is updated by the app when the collection gesture is 'embedded'
          const hasFocus = rootRef.current?.contains(document.activeElement as HTMLElement);
          if ((!isCurrentItemOverridden && hasFocus) || isEmbedded) {
            scrollIntoView(elem, rootRef, viewportConfig, scrollToVisibleOffset);
          }
        }
      }
    }
    // listen to data because the current item would re-render when data is updated, i.e. the id
    // of current item would be updated, so the currentKey itself couldn't guarantee the root has
    // the latest aria-activedescendant
  }, [
    data,
    rootRef,
    currentKey,
    idExtractor,
    viewportConfig,
    scrollToVisibleOffset,
    isCurrentItemOverridden,
    allowActiveDescendant,
    isEmbedded
  ]);

  return {
    navigationProps: mergeProps(
      currentKeyProps,
      focusRingProps,
      allowTabbableMode ? tabbableModeProps : {},
      {
        onFocus: handleFocus,
        onPointerDown: handlePointerDown
      }
    ),
    currentKey,
    onCurrentKeyChange,
    isCurrentItemOverridden,
    showFocusRing,
    isCurrentTabbableKey
  };
};

/**
 * A private helper function to handle scroll into view
 */
const scrollIntoView = <K, D>(
  elem: Element,
  rootRef: RefObject<HTMLDivElement>,
  viewportConfig: CommonProps<K, D>['viewportConfig'],
  scrollToVisibleOffset: CommonProps<K, D>['scrollToVisibleOffset']
) => {
  if (rootRef.current) {
    const scroller = viewportConfig?.scrollerRef.current;
    const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
    scrollToVisible(elem, scroller || rootRef.current, offset);
  }
};

// TODO: should update the class 'oj-listview-expander' to something more general
// when we add treeview or expander components
const isExpander = (elem: HTMLElement) => {
  if (elem.parentElement && elem.parentElement.classList.contains('oj-listview-expander')) {
    return true;
  }
  return false;
};
